<template v-if="isReady">
    <div class="product-customizer-container" id="product-customizer">
        <div class="product-customizer-background"></div>
        <div class="product-customizer grid-container" ref="customizerContainer">
            <div class="grid-x grid-padding-x scroll-container" @scroll="onScroll">
                <div class="cell product-image" v-if="image">
                    <!-- {{  image  }} -->
                    <picture>
                        <source media="(max-width: 640px)" :srcset="`${image.card} 1x, ${image.card_2x} 2x`">
                        <source media="(min-width: 640px)" :srcset="`${image.card} 1x, ${image.card_2x} 2x`">
                        <img alt="Product Image" :src="`${image.card}`">
                    </picture>
                </div>
                <div class="cell customizer-content">
                    <p :class="['close-button', {static: !image}]"><a @click="close" v-html="Icon.CROSS"></a></p>

                    <!-- Product Name Container -->
                    <div :class="['grid-x', 'product-name-container', 'align-middle', {'hover': arrowDisplayed}]" ref="productNameContainer">
                        <div :class="['cell', 'shrink', 'arrow-container', {'arrow-displayed': arrowDisplayed}]">
                            <p class="close-button-hover margin-0" @click="close" v-html="Icon.CROSS"></p>
                        </div>

                        <!-- Product Name & Price -->
                        <div :class="['cell', 'auto', 'product-name', {'arrow-displayed': arrowDisplayed}]">
                            <h3 class="bold margin-bottom-0" v-if="product">{{ product.name }}</h3>
                        </div>
                    </div>

                    <!-- Customizer Content -->
                    <div class="customizer-container tall">
                        <p class="color-battleship-gray product-description" v-if="product">{{ product.description }}</p>
                        <customization-step v-for="step in steps" :key="step.id" :dusk="'step-' + step.id" :content="step"></customization-step>
                        <div class="special-instructions customizer-padding" ref="instructions">
                            <text-input
                                dusk="instructions"
                                :textarea="true"
                                :textarea-rows="4"
                                :value="instructions"
                                @change="setProductInstructions($event)"
                                placeholder="Please let us know if you have any allergies or if you want to remove ingredients."
                            ></text-input>
                        </div>
                    </div>

                    <!-- Bottom Options -->
                    <div class="align-middle cart-options grid-x align-justify customizer-padding">
                        <div class="quantity cell shrink">
                            <number-input
                                dusk="quantity"
                                :value="quantity"
                                :allow-negative="false"
                                :minimum-amount="1"
                                :allow-positive="!maxProductsReached(1)"
                                @change="setQuantity($event.value)"
                            ></number-input>
                        </div>
                        <div class="cell shrink">
                            <button dusk="add-to-cart" class="button margin-0" :disabled="!isProductReady || maxProductsReached(0)" @click="addToCart">{{ addToCartLabel }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import ProductCustomizerModule from '@/stores/modules/product-customizer';
import FoodModule from '@/stores/modules/food';
import CartModule from '@/stores/modules/cart';
import { getModule } from 'vuex-module-decorators';
import Product from '@/models/product';
import ProductType from '@/models/product-type';
import Step, { StepType } from '@/models/step';
import TextInput from '@/components/common/forms/TextInput.vue';
import NumberInput from '@/components/common/forms/NumberInput.vue';
import CustomizationStep from './CustomizationStep.vue';
import { Icon } from '@/front/icons';
import Settings, { SettingNamespace } from '@/utils/settings';
import { pushGaEvent } from '@/utils/analytics';
import UserModule from '@/stores/modules/user';

Component.registerHooks([
    'beforeRouteUpdate'
]);

@Component({
    components: { TextInput, NumberInput, CustomizationStep }
})
export default class ProductCustomizer extends Vue
{
    productCustomizerModule!: ProductCustomizerModule;
    foodModule!: FoodModule;
    cartModule!: CartModule;
    userModule!: UserModule;
    steps: Step[] = [];
    arrowDisplayed: boolean = false;


    created() {
        this.productCustomizerModule = getModule(ProductCustomizerModule, this.$store);
        this.foodModule = getModule(FoodModule, this.$store);
        this.cartModule = getModule(CartModule, this.$store);
        this.userModule = getModule(UserModule, this.$store);
    }


    async mounted() {
        const settings = await Settings.getInstance();
        const storeName = settings.get(SettingNamespace.GENERAL, 'store_name')?.value;
        if (this.$route.params.productSlug && this.$route.params.productId) {
            const product = this.foodModule.products.find((product: Product) => product.id === Number(this.$route.params.productId));
            if (product) {
                this.productCustomizerModule.setCurrentProduct(product);
                if (product.type) {
                    const productType = new ProductType(product.type);
                    this.productCustomizerModule.setProductType(productType);
                    const orderFlow = this.foodModule.orderFlows.find((orderFlow) => orderFlow.id === productType.order_flow_id);
                    if (orderFlow) {
                        const steps = Step.createBatch(orderFlow.steps);
                        this.productCustomizerModule.setStepsIngredients(steps);
                        this.productCustomizerModule.setStepsProductOptions(steps);
                        this.steps = steps.sort((a: Step, b: Step) => {
                            if (a.position < b.position) return -1;
                            return 1;
                        });
                    }
                }
                document.title = product.name + ' - ' + storeName;
                pushGaEvent({
                    event: 'view_item',
                    ecommerce: {
                        currency: 'KYD',
                        value: product.price,
                        items: [
                            {
                                item_id: product.id!.toString(),
                                item_name: product.is_preset ? product.name : 'Custom - ' + product.name,
                                quantity: product.quantity!,
                                price: product.price,
                                item_category: product.type.name,
                            }
                        ]
                    }
                });
            } else {
                return this.close();
            }
        }
    }

    get products() {
        return this.foodModule.products.length ? this.foodModule.products : null;
    }

    get isReady() {
        return this.foodModule.isReady;
    }

    async addToCart() {
        const cartProduct = this.productCustomizerModule.cartProduct;
        if (cartProduct) {
            const product = new Product(cartProduct);
            this.cartModule.addProduct(product);
            pushGaEvent({
                event: 'add_to_cart',
                ecommerce: {
                    currency: 'KYD',
                    value: product.price,
                    items: [
                        {
                            item_id: product.origin ? product.origin.id!.toString() : product.id!.toString(),
                            item_name: product.is_preset ? product.name : 'Custom - ' + product.name,
                            quantity: product.quantity!,
                            price: product.price,
                            item_category: product.type.name,
                        }
                    ]
                }
            });
        }
        this.close();
    }

    close() {
        this.productCustomizerModule.reset();
        return this.$router.push({name: 'food'}).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err);
            }
        });
    }

    setProductInstructions(value: string) {
        this.productCustomizerModule.setProductInstructions(value);
    }

    setQuantity(quantity: number) {
        this.productCustomizerModule.setQuantity(quantity);
    }

    onScroll() {
        if (<HTMLElement>this.$refs.customizerContainer && <HTMLElement>this.$refs.productNameContainer) {
            const containerOffset = (<HTMLElement>this.$refs.customizerContainer).getBoundingClientRect().top;
            const nameOffset = (<HTMLElement>this.$refs.productNameContainer).getBoundingClientRect().top;
            if (nameOffset - containerOffset <= 0) {
                return this.arrowDisplayed = true;
            }
            this.arrowDisplayed = false;
        }
        this.arrowDisplayed = false;
    }

    get isStepMissingIngredients() {
        return (step: Step) => this.productCustomizerModule.stepIngredientsCount(step) < step.min_items;
    }

    get isStepMissingProductOptions() {
        return (step: Step) => this.productCustomizerModule.stepProductOptionsCount(step) < step.min_items;
    }

    get isStepCustomisable() {
        return (step: Step) => {
            if (this.product) {
                return step.is_customisable;
            }
            return true;
        }
    }

    get isProductReady() {
        for (let i in this.steps) {
            if (this.steps[i].type === StepType.INGREDIENTS && this.isStepMissingIngredients(this.steps[i])) {
                return false;
            }
            if (this.steps[i].type === StepType.OPTIONS && this.isStepMissingProductOptions(this.steps[i])) {
                return false;
            }
        }
        return true;
    }

    get maxProductsReached() {
        return (removeQty: number) => this.cartModule.productsQuantity + this.quantity + removeQty > this.cartModule.maxOrderProducts;
    }

    get image() {
        if (this.product && this.product.image) {
            return this.product.image;
        }
        return this.productType?.image;
    }

    get product() {
        if (this.isReady && this.productCustomizerModule.currentProduct) {
            return this.productCustomizerModule.currentProduct;
        }
        return null;
    }

    get productPrice() {
        if (this.product || this.productType?.custom_price) {
            return this.productCustomizerModule.productPrice * this.quantity;
        }
    }

    get parsePrice() {
        return (price: number) => {
            return (price % 1 !== 0) ? price.toFixed(2) : price;
        }
    }

    get addToCartLabel() {
        const maxProductsReached = this.maxProductsReached(0);
        if (this.productPrice !== undefined) {
            const price  = this.parsePrice(this.productPrice);
            return (!maxProductsReached) ? `Add to cart • $${price}` : 'Cart full';
        }
        return (!maxProductsReached) ? 'Add to cart' : 'Cart full';
    }

    get instructions() {
        return this.productCustomizerModule.productInstructions;
    }

    get productType() {
        return this.productCustomizerModule.productType;
    }

    get quantity() {
        return this.productCustomizerModule.quantity;
    }

    get Icon() {
        return Icon;
    }
}
</script>
<style lang="scss">
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.product-customizer-container {
    @include breakpoint(medium down) {
        background: white;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 12;
    }
}
.product-customizer {
    @include breakpoint(large) {
        width: 450px;
        max-width: $global-width;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: white;
        border-radius: var(--customizer-border-radius);
        box-shadow: 3px 6px 13px 0 rgba(0, 0, 0, 0.1);
        z-index: 10;
        overflow: hidden;
        max-height: 70vh;
        min-height: 50vh;
    }

    @include breakpoint(medium down) {
        height: 100vh;
        overflow-y: scroll;
    }

    & > .grid-x {
        height: 100%;
    }

    .customizer-padding {
        padding: 0 25px;
    }

    .scroll-container {
        position: relative;
        overflow-y: auto;
        @include breakpoint(large) {
            max-height: 70vh;
            min-height: 50vh;
        }
    }

    .customizer-content {
        padding: 0;

        @include breakpoint(medium down) {
            background: none;
        }

        .product-name-container {
            position: sticky;
            top: 0px;
            z-index: 50;
            height: 60px;
            background-color: white;
            transition: box-shadow ease 0.3s;
            border-top-left-radius: var(--customizer-name-radius);
            border-top-right-radius: var(--customizer-name-radius);
            padding: 15px 15px 0px;

            &.hover {
                border-radius: 0;
                box-shadow: 0 6px 18px 0 rgba(0, 0, 0, 0.1);
                padding: 8px 15px 5px;
            }

            .arrow-container {
                opacity: 0;
                transition: opacity 0.3s ease;

                &.arrow-displayed {
                    opacity: 1;
                }
            }

            .product-price-container {
                padding: 0 25px;
                @include breakpoint(medium down) {
                    padding: 0 5px;
                }
            }

            .product-name {
                padding: 0;
                transition: margin 0.3s ease;
                margin-left: -20px;

                &.arrow-displayed {
                    margin-left: 10px;
                }

                h3 {
                    color: var(--customizer-title-color);
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-size: 25px;
                }

            }
        }

        .customizer-container {
            position: relative;
            background: var(--white);
            height: calc(100% - 132px); // Remove customizer header from height

            .product-description {
                padding: 10px 25px 0;
            }

            .special-instructions {
                margin-top: 40px;
                margin-bottom: 110px;

                @include breakpoint(small only) {
                    margin-bottom: 200px;
                }
            }

            .instructions-button {
                cursor: pointer;
            }
        }

        .close-button {
            font-size: 35px;
            position: fixed;
            top: 15px;
            left: 15px;
            width: 30px;

            &.static {
                position: relative;
            }

            &-hover {
                height: 30px;
            }
            &:hover {
                circle {
                    fill: var(--light-gray);
                }
            }
        }

        .product-price {
            @include breakpoint(medium down) {
                display: inline;
            }
        }

        .cart-options {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 15px 25px;
            margin: 0;
            box-shadow: 0 -2px 8px 0 rgba(0, 0, 0, 0.06);
            background-color: var(--white);
            z-index: 15;
            border-top-right-radius: var(--customizer-options-radius);
            border-top-left-radius: var(--customizer-options-radius);

            @include breakpoint(large up) {
                border-bottom-right-radius: var(--customizer-border-radius);
                border-bottom-left-radius: var(--customizer-border-radius);
            }

            @include breakpoint(medium down) {
                padding: 15px;
            }
        }
    }

    .product-image {
        padding: 0;
        width: 100%;
        margin-bottom: -30px;
        background-color: #F1F2F4;

        img {
            object-fit: cover;
            height: 280px;
            width: 100%;
        }

        @include breakpoint(medium down) {
            border-radius: 0;
            height: 30vh;
            width: 100%;
            margin-bottom: -30px;
        }
    }
}

.product-customizer-background {
    @include breakpoint(large) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.5);
        z-index: 5;
        backdrop-filter: blur(5px);
    }
}
</style>
